import React, { useState, useRef } from 'react';
import { vendorUploadPreview } from '../../services/liveStamping';

const VendorSampleUpload = ({ order, onSuccess, onClose }) => {
  const [previewFile, setPreviewFile] = useState(null);
  const [previewThumb, setPreviewThumb] = useState(null);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const isValid = previewFile && certificateNumber.trim() && issueDate;

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setError('Preview must be PNG or JPEG.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }
    setError('');
    setPreviewFile(file);
    const reader = new FileReader();
    reader.onload = e => setPreviewThumb(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError('');
    try {
      await vendorUploadPreview(order.id, previewFile, {
        certificate_number: certificateNumber.trim(),
        issue_date: new Date(issueDate).toISOString(),
      });
      onSuccess();
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
            aria-label="Back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h3 className="font-bold text-gray-800">Stage 1 — Upload Preview</h3>
            <p className="text-xs text-gray-500 mt-0.5">Order #{order.id} · {order.service_name}</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Body — two columns */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: image dropzone */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Preview Image <span className="text-red-500">*</span></p>
            <input
              type="file"
              ref={fileInputRef}
              accept=".png,.jpg,.jpeg"
              className="hidden"
              onChange={e => handleFileSelect(e.target.files[0])}
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              className="border-2 border-dashed border-indigo-200 bg-indigo-50/50 rounded-xl p-6 flex flex-col items-center cursor-pointer hover:bg-indigo-50 transition-colors min-h-[160px] justify-center"
            >
              {previewThumb ? (
                <img src={previewThumb} alt="Preview" className="max-h-36 rounded border border-gray-200 shadow-sm object-contain" />
              ) : (
                <>
                  <svg className="w-10 h-10 text-indigo-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm font-medium text-indigo-700">Click or drag to upload</p>
                  <p className="text-xs text-gray-400 mt-1">PNG or JPEG, max 5MB</p>
                </>
              )}
            </div>
            {previewFile && (
              <p className="text-xs text-gray-500 mt-1 truncate">{previewFile.name} ({(previewFile.size / 1024 / 1024).toFixed(2)} MB)</p>
            )}
          </div>

          {/* Right: metadata form */}
          <div className="space-y-4">
            <p className="text-sm font-semibold text-gray-700">Stamp Metadata</p>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Certificate Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={certificateNumber}
                onChange={e => setCertificateNumber(e.target.value)}
                maxLength={100}
                placeholder="e.g. IN-MH12345678901234"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Certificate Issue Date &amp; Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={issueDate}
                onChange={e => setIssueDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <p className="text-xs text-gray-400 mt-1">As shown on the government e-stamp portal</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
            {submitting ? 'Uploading…' : 'Submit Preview'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorSampleUpload;
